import { useCallback, useState, type RefObject } from "react";
import { useDgaApi } from "src/components/DealerGeneratedApplication/hooks/useDgaApi";
import { useApplicationLock } from "src/components/DealerGeneratedApplication";
import { useEmployeeCache } from "src/components/DealerGeneratedApplication/hooks/useEmployeeCache";
import { useToast } from "src/components/Toast";
import type { DgaLockChannel } from "src/utils/dgaLockChannel";

const UNLOCK_POLL_INTERVAL_MS = 200;
const UNLOCK_POLL_MAX_ATTEMPTS = 20;

async function waitForUnlock(
  appId: string,
  getApplication: ReturnType<typeof useDgaApi>["getApplication"],
): Promise<void> {
  for (let i = 0; i < UNLOCK_POLL_MAX_ATTEMPTS; i++) {
    try {
      const result = await getApplication(appId);
      const lockedByUserId = getLockedByUserId(result.data);
      if (result.status === 200 && !lockedByUserId) {
        return;
      }
    } catch {
      // Treat failures as "still locked" and keep polling until timeout.
    }
    await new Promise((resolve) =>
      setTimeout(resolve, UNLOCK_POLL_INTERVAL_MS),
    );
  }
}

export interface OpenDgaModalWithLockApp {
  id: string;
  is_locked?: boolean;
  locked_by_user_id?: string;
}

export interface OpenDgaModalWithLockOptions {
  onLockStatusResolved?: (
    appId: string,
    status: { is_locked: boolean; locked_by_user_id?: string },
  ) => void;
}

function getLockedByUserId(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const root = data as { locked_by_user_id?: unknown };
  if (typeof root.locked_by_user_id === "string" && root.locked_by_user_id) {
    return root.locked_by_user_id;
  }
  const nested = data as {
    application?: { locked_by_user_id?: unknown };
  };
  if (
    typeof nested.application?.locked_by_user_id === "string" &&
    nested.application.locked_by_user_id
  ) {
    return nested.application.locked_by_user_id;
  }
  return undefined;
}

export function useOpenDgaModalWithLock(
  dealerId: string | undefined,
  lockChannelRef: RefObject<DgaLockChannel | null>,
  openDgaModal: (applicationId?: string) => void,
) {
  const { lockApplication, getApplication } = useDgaApi();
  const { takeOverLock } = useApplicationLock(undefined, dealerId);
  const { getEmployeeUser } = useEmployeeCache(dealerId);
  const { setPendingToast } = useToast();

  const [checkingLockAppId, setCheckingLockAppId] = useState<string | null>(
    null,
  );
  const [showTakeoverDialog, setShowTakeoverDialog] = useState(false);
  const [takeoverAppId, setTakeoverAppId] = useState<string | null>(null);
  const [takeoverLockedByUser, setTakeoverLockedByUser] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null>(null);
  const [isTakingOver, setIsTakingOver] = useState(false);

  const showTakeoverDialogFor = useCallback(
    (appId: string, lockedByUserId?: string) => {
      setTakeoverAppId(appId);
      setTakeoverLockedByUser(
        lockedByUserId ? getEmployeeUser(lockedByUserId) : null,
      );
      setShowTakeoverDialog(true);
    },
    [getEmployeeUser],
  );

  const openDgaModalWithLock = useCallback(
    (app: OpenDgaModalWithLockApp, options?: OpenDgaModalWithLockOptions) => {
      void (async () => {
        setCheckingLockAppId(app.id);

        try {
          let latest = app;
          try {
            const applicationResp = await getApplication(app.id);
            const lockedByUserId = getLockedByUserId(applicationResp.data);
            latest = {
              ...app,
              is_locked: !!lockedByUserId,
              locked_by_user_id: lockedByUserId,
            };
            options?.onLockStatusResolved?.(app.id, {
              is_locked: !!lockedByUserId,
              locked_by_user_id: lockedByUserId,
            });
          } catch {
            // Fall back to table snapshot if fetch fails.
          }

          if (latest.is_locked && latest.locked_by_user_id) {
            showTakeoverDialogFor(app.id, latest.locked_by_user_id);
            return;
          }

          const lockResult = await lockApplication(app.id);

          if (lockResult.status === 200) {
            options?.onLockStatusResolved?.(app.id, { is_locked: false });
            openDgaModal(app.id);
          } else {
            const refreshResp = await getApplication(app.id);
            const lockedByUserId = getLockedByUserId(refreshResp.data);
            if (lockedByUserId) {
              options?.onLockStatusResolved?.(app.id, {
                is_locked: true,
                locked_by_user_id: lockedByUserId,
              });
              showTakeoverDialogFor(app.id, lockedByUserId);
            } else {
              options?.onLockStatusResolved?.(app.id, { is_locked: false });
              setPendingToast(
                "This application is currently being edited by another user",
                "error",
              );
            }
          }
        } catch {
          setPendingToast(
            "There was a problem retrieving the application details",
            "error",
          );
        } finally {
          setCheckingLockAppId(null);
        }
      })();
    },
    [
      getApplication,
      lockApplication,
      openDgaModal,
      setPendingToast,
      showTakeoverDialogFor,
    ],
  );

  const handleTakeover = useCallback(async () => {
    if (!takeoverAppId) return;
    setIsTakingOver(true);
    try {
      lockChannelRef.current?.postTakeover(takeoverAppId);
      await waitForUnlock(takeoverAppId, getApplication);
      await takeOverLock(takeoverAppId);
      openDgaModal(takeoverAppId);
    } finally {
      setIsTakingOver(false);
    }
  }, [
    takeoverAppId,
    lockChannelRef,
    getApplication,
    takeOverLock,
    openDgaModal,
  ]);

  const closeTakeoverDialog = useCallback(() => {
    setShowTakeoverDialog(false);
    setTakeoverAppId(null);
    setTakeoverLockedByUser(null);
  }, []);

  return {
    checkingLockAppId,
    openDgaModalWithLock,
    showTakeoverDialog,
    setShowTakeoverDialog,
    closeTakeoverDialog,
    takeoverAppId,
    takeoverLockedByUser,
    isTakingOver,
    handleTakeover,
  };
}
